<!--
  directの組織選択ドロップボックス
  ナビバーに配置されることを想定
-->
<template>
  <b-nav-item-dropdown
    :text="selectedDomainName"
    class="domain-dropdown"
    toggle-class="toggle-class"
    :disabled="disabled"
    @shown="expanded = true"
    @hidden="expanded = false"
    no-caret
  >
    <template #button-content>
      <span class="domain-dropdown-label">{{ selectedDomainName }}</span>
      <b-icon-chevron-up v-if="expanded" class="domain-dropdown-toggle-icon"></b-icon-chevron-up>
      <b-icon-chevron-down v-else class="domain-dropdown-toggle-icon"></b-icon-chevron-down>
    </template>
    <b-dropdown-item
      v-for="item in domainItems"
      :key="item.domain.domain_id"
      :disabled="item.state == 'off'"
      @click="onDomainSelected(item.domain.domain_id_str)"
    >
      <!-- アプリOFF(選択中かどうかより優先して判定する) -->
      <div v-if="item.state == 'off'" class="app-off">
        <span class="app-off-label">{{ item.domain.domain_name }}</span
        ><span class="app-off-warning">掲示板の利用はありません</span>
      </div>
      <!-- 選択中 -->
      <div v-else-if="item.state == 'selected'" class="selected-domain-label">
        {{ item.domain.domain_name }}
      </div>
      <!-- アプリON -->
      <div v-else>{{ item.domain.domain_name }}</div>
    </b-dropdown-item>
  </b-nav-item-dropdown>
</template>
<style lang="scss">
.domain-dropdown {
  .domain-dropdown-label {
    font-size: 20px;
  }
  .domain-dropdown-toggle-icon {
    margin-left: 10px;
    margin-right: 10px;
    width: 14px;
    color: #212529;
  }
}
.domain-dropdown > a {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
}
.domain-dropdown .dropdown-menu.show {
  max-height: 50vh;
  max-width: 70vw;
  overflow-x: hidden;
  overflow-y: overlay;
}
.toggle-class > span {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// スマホ用設定
@media (max-width: 768px) {
  .toggle-class > span {
    max-width: 200px;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.selected-domain-label {
  color: #00b7c6;
}

.dropdown-item > div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// テキスト色
$text-color: white;
.nav-item.domain-dropdown,
.nav-item.domain-dropdown.show {
  .nav-link.dropdown-toggle {
    color: $text-color;
    &:focus {
      color: $text-color;
    }
  }
}

// 掲示板利用が無い項目の表示
.dropdown-item {
  // 掲示板利用されていない場合、禁止マークを出すため(pointer-events: none の打消)
  &.disabled {
    pointer-events: auto !important;
  }
  .app-off {
    width: 100%;
    display: inline-flex;
    column-gap: 5px;
    color: #bfbfbf;
    cursor: not-allowed;

    .app-off-label {
      flex-shrink: 1;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    .app-off-warning {
      flex-shrink: 0;
      margin-left: auto;
      color: black;
    }
  }
}
// テキスト色：PC用設定
@media (min-width: 768px) {
  $text-color: black;
  .nav-item.domain-dropdown,
  .nav-item.domain-dropdown.show {
    .nav-link.dropdown-toggle {
      color: $text-color;
      &:focus {
        color: $text-color;
      }
    }
  }
}
</style>

<script lang="ts">
import { computed, defineComponent, nextTick, ref, watch, type PropType } from "vue";
import { useAppStore } from "@/store";
import type { SetPayload } from "../../store/domain-store";
import type { AppKeys } from "../../direct-app-config";
import type { DownloadDeviceTypes } from "@/suppport-attachment-types";
import {
  DefaultAllowAttachmentTypeByClient,
  DownloadDeviceTypesDefault,
} from "@/suppport-attachment-types";
import type { Domains } from "@/direct-access";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import type { DomainItemState } from "./domain-item-state";
import { domainItemState } from "./domain-item-state";

/** ドロップダウンの1項目 */
type DomainItem = { domain: Domains[number]; state: DomainItemState };

export default defineComponent({
  name: "direct-domain-dropdown",
  props: {
    domainId: { type: String, default: () => "" }, // 選択組織名初期ロード用
    domains: { type: Array as PropType<Domains>, default: () => [] },
  },
  setup(props) {
    const rootEvent = useLegacyRootEvent();

    /** 選択された組織ID */
    const selectedDomainId = ref<string>("");

    // 開閉状態の管理
    const expanded = ref(false);

    watch(
      () => props.domainId,
      (value: string): void => {
        selectedDomainId.value = value;
      },
      { immediate: true }
    );

    const selectedDomainName = computed((): string => {
      if (selectedDomainId.value) {
        const domain = props.domains.find(
          (domain) => domain.domain_id_str == selectedDomainId.value
        );
        if (domain) return domain.domain_name;
      }
      return "";
    });

    const disabled = computed((): boolean => {
      return useAppStore().isLoading;
    });

    /** 表示状態(off: 掲示板が利用できない組織)を添えた組織一覧 */
    const domainItems = computed((): DomainItem[] => {
      return props.domains.map((domain) => ({
        domain,
        state: domainItemState(domain, selectedDomainId.value),
      }));
    });

    const onDomainSelected = (domainIdStr: string): void => {
      selectedDomainId.value = domainIdStr;
      rootEvent.emit("on-domain-selected", domainIdStr);

      const domain = props.domains.find((domain) => domain.domain_id_str == selectedDomainId.value);
      if (domain) {
        // dropdownから組織変更する際に検索条件をリセット
        rootEvent.emit("on-set-search-param", { word: "" });
        const allow_attachment_type = domain.setting.allow_attachment_type || 0; // 0, null: false, 1~: true
        const allow_save_attachment = (domain.setting.allow_save_attachments ||
          DownloadDeviceTypesDefault) as DownloadDeviceTypes;
        const allow_attachment_type_by_client =
          domain.setting.allow_attachment_type_by_client || DefaultAllowAttachmentTypeByClient;
        const open_talk_enabled = domain.setting.open_talk_enabled || false;
        // アップロード先 設定場所 (domainSetting の upload_pathにセット)
        // const upload_path = "uploads";
        const domainSetting: SetPayload = {
          allow_attachment_type: allow_attachment_type,
          allow_save_attachments: allow_save_attachment,
          allow_attachment_type_by_client: allow_attachment_type_by_client,
          open_talk_enabled: open_talk_enabled,
          domainId: domain.domain_id_str,
          domainName: domain.domain_name,
          domainRole: domain.role || undefined,
          solution_ids: domain.contract.solution_ids || [],
          direct_app_keys: (domain.contract.direct_app_keys || []) as AppKeys[],
        };
        rootEvent.emit("on-set-domain-setting", domainSetting);
        void nextTick(() => {
          const domainId = useAppStore().domainId;
          const topicId = useAppStore().topicId;
          useAppStore().fetchData({ domainId, topicId });
        });
      }
    };

    return {
      selectedDomainId,
      expanded,
      selectedDomainName,
      disabled,
      domainItems,
      onDomainSelected,
    };
  },
});
</script>
